import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { getApiBaseUrl } from '../config/api';
import { maskEngineChassisNumber, maskChassisNumber } from '../utils/maskUtils';
import './DeclarationsTable.css';

const DeclarationsTable = ({ user, onShowNotification }) => {
  const API_BASE_URL = getApiBaseUrl();
  const [declarations, setDeclarations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('issuance_on');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedDeclarations, setSelectedDeclarations] = useState([]);
  const [filterCertificateType, setFilterCertificateType] = useState('all');
  const [filterAgentId, setFilterAgentId] = useState('');
  const [agents, setAgents] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [agentStats, setAgentStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [selectedDeclaration, setSelectedDeclaration] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    loadDeclarations();
    if (filterAgentId) {
      loadAgentStats(filterAgentId);
    } else {
      setAgentStats(null);
    }
  }, [currentPage, itemsPerPage, searchTerm, sortField, sortDirection, filterCertificateType, filterAgentId]);

  const loadAgentStats = async (agentId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/agents/${agentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setAgentStats(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading agent stats:', error);
    }
  };

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-dropdown')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  const loadAgents = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/agents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.agents) {
          setAgents(result.agents);
        }
      }
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const loadDeclarations = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      
      if (filterAgentId) {
        params.append('agentId', filterAgentId);
      }
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/declarations?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setDeclarations(result.declarations || []);
          setPagination(result.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 });
        } else {
          throw new Error(result.error || 'Failed to load declarations');
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to load declarations' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('Error loading declarations:', error);
      if (onShowNotification) {
        onShowNotification(error.message || 'Error loading declarations. Please try again.', 'error');
      }
      setDeclarations([]);
      setPagination({ page: 1, limit: 10, total: 0, totalPages: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-KE', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-KE', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'KES 0';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleExport = (format) => {
    const dataToExport = declarations.length > 0 ? declarations : [];
    
    if (format === 'pdf') {
      exportToPDF(dataToExport);
    } else if (format === 'excel') {
      exportToExcel(dataToExport);
    }
  };

  const exportToPDF = (data) => {
    const doc = new jsPDF('landscape');
    
    // Title
    doc.setFontSize(16);
    doc.text('DMVIC Declarations Report', 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString('en-KE')}`, 14, 22);
    
    // Table data
    const tableData = data.map(decl => [
      decl.row_number || '',
      decl.agency_name || 'N/A',
      decl.certificate_number || 'N/A',
      decl.ref_number || 'N/A',
      decl.certificate_type || 'N/A',
      decl.policy_number || 'N/A',
      decl.vehicle_number || 'N/A',
      decl.insured_person || 'N/A',
      formatDate(decl.start_date),
      formatDate(decl.end_date),
      formatCurrency(decl.premium)
    ]);
    
    doc.autoTable({
      head: [['#', 'Agent', 'Certificate #', 'Ref #', 'Type', 'Policy #', 'Vehicle #', 'Insured', 'Start Date', 'End Date', 'Premium']],
      body: tableData,
      startY: 28,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [6, 78, 59] },
      margin: { top: 28 }
    });
    
    doc.save(`declarations-report-${new Date().toISOString().split('T')[0]}.pdf`);
    
    if (onShowNotification) {
      onShowNotification('PDF report generated successfully!', 'success');
    }
  };

  const exportToExcel = (data) => {
    // Prepare data matching Excel template format
    const excelData = data.map(decl => ({
      '#': decl.row_number || '',
      'Agent': decl.agency_name || '',
      'IRA Number': decl.ira_number || '',
      'Certificate #': decl.certificate_number || '',
      'Ref #': decl.ref_number || '',
      'Certificate Type': decl.certificate_type || '',
      'Insurance Company': decl.insurance_company || '',
      'Intermediary': decl.intermediary || '',
      'Issued By': decl.issued_by || '',
      'Insured Person': decl.insured_person || '',
      'Issuance On': decl.issuance_on ? formatDateTime(decl.issuance_on) : '',
      'Policy #': decl.policy_number || '',
      'Vehicle #': decl.vehicle_number || '',
      'Chassis #': decl.chassis_number ? maskChassisNumber(String(decl.chassis_number)) : '',
      'Start Date': decl.start_date ? formatDate(decl.start_date) : '',
      'End Date': decl.end_date ? formatDate(decl.end_date) : '',
      'Vehicle Make': decl.vehicle_make || '',
      'Vehicle Model': decl.vehicle_model || '',
      'Body Type': decl.body_type || '',
      'Year of Manufacture': decl.year_of_manufacture || '',
      'Engine Number': decl.engine_number ? maskEngineChassisNumber(String(decl.engine_number)) : '',
      'Insured PIN': decl.insured_pin || '',
      'Sum Insured': decl.sum_insured || 0,
      'Email': decl.email || '',
      'Phone Number': decl.phone_number || '',
      'Certificate Status': decl.certificate_status || '',
      'Premium': decl.premium || 0
    }));
    
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Declarations');
    
    XLSX.writeFile(wb, `declarations-report-${new Date().toISOString().split('T')[0]}.xlsx`);
    
    if (onShowNotification) {
      onShowNotification('Excel report generated successfully!', 'success');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewDetails = (declaration) => {
    setSelectedDeclaration(declaration);
    setShowModal(true);
  };

  const filteredDeclarations = declarations.filter(decl => {
    const matchesSearch = !searchTerm || 
      (decl.certificate_number && decl.certificate_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (decl.policy_number && decl.policy_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (decl.vehicle_number && decl.vehicle_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (decl.insured_person && decl.insured_person.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = filterCertificateType === 'all' || decl.certificate_type === filterCertificateType;
    
    return matchesSearch && matchesType;
  });

  return (
    <div style={{ width: '100%', maxWidth: 'min(1600px, calc(95vw - 240px))', margin: '0 auto', marginLeft: 0, padding: 'clamp(1rem, 2vh, 2rem) clamp(1rem, 2.5vw, 2rem)', paddingLeft: 'clamp(1.5rem, 3vw, 2.5rem)', boxSizing: 'border-box', minWidth: 0 }}>
      <div className="declarations-table-container">
        <div className="table-header">
        <div className="header-left">
          <p>
            {filterAgentId 
              ? `Viewing covers for selected agent (${pagination.total || 0} total)` 
              : `View all agent covers and declarations (${pagination.total || 0} total)`}
          </p>
        </div>
        <div className="header-actions">
          <input
            type="text"
            placeholder="Search declarations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            🔍 Filters
          </button>
          {user?.role === 'admin' && (
            <div className="export-dropdown">
              <button 
                className="export-btn"
                onClick={() => setShowExportMenu(!showExportMenu)}
              >
                📊 Export
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={() => {
                    handleExport('pdf');
                    setShowExportMenu(false);
                  }}>
                    📄 Export as PDF
                  </button>
                  <button onClick={() => {
                    handleExport('excel');
                    setShowExportMenu(false);
                  }}>
                    📊 Export as Excel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Agent Filter - Always Visible */}
      <div className="agent-filter-section">
        <div className="agent-filter-header">
          <div className="filter-label-group">
            <label className="filter-label">
              <span className="filter-icon">👤</span>
              Filter by Agent:
            </label>
            {filterAgentId && (
              <button
                className="clear-filter-btn"
                onClick={() => {
                  setFilterAgentId('');
                  setCurrentPage(1);
                }}
                title="Clear agent filter"
              >
                ✕ Clear Filter
              </button>
            )}
          </div>
          <select
            className="agent-select"
            value={filterAgentId}
            onChange={(e) => {
              setFilterAgentId(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Agents ({pagination.total || 0} covers)</option>
            {agents.map(agent => (
              <option key={agent.id} value={agent.id}>
                {agent.agency_name} ({agent.ira_number}) - {agent.total_transactions || 0} covers
              </option>
            ))}
          </select>
        </div>
        
        {/* Agent Statistics Card */}
        {filterAgentId && agentStats && (
          <div className="agent-stats-card">
            <div className="agent-stats-header">
              <h3>📊 {agentStats.agency_name} - Business Overview</h3>
              <p className="ira-number" style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.85, color: 'white' }}>
                IRA: {agentStats.ira_number || 'N/A'}
              </p>
            </div>
            <div className="agent-stats-grid">
              <div className="stat-item">
                <span className="stat-label">Total Covers</span>
                <span className="stat-value">{agentStats.total_transactions || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Revenue</span>
                <span className="stat-value">{formatCurrency(agentStats.total_revenue || 0)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">IRA Number</span>
                <span className="stat-value">{agentStats.ira_number}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Employees</span>
                <span className="stat-value">{agentStats.total_users || 0}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Certificate Type:</label>
            <select
              value={filterCertificateType}
              onChange={(e) => setFilterCertificateType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="Type A - PSV Unmarked">Type A - PSV Unmarked</option>
              <option value="Type B - Commercial Vehicle">Type B - Commercial Vehicle</option>
              <option value="Type C - Motor Private">Type C - Motor Private</option>
              <option value="Type D - Motor Cycle">Type D - Motor Cycle</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Items per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading declarations...</p>
        </div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="declarations-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('row_number')}>
                    # {sortField === 'row_number' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('agency_name')}>
                    Agent {sortField === 'agency_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('certificate_number')}>
                    Certificate # {sortField === 'certificate_number' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('policy_number')}>
                    Policy # {sortField === 'policy_number' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('certificate_type')}>
                    Type {sortField === 'certificate_type' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('vehicle_number')}>
                    Vehicle # {sortField === 'vehicle_number' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('insured_person')}>
                    Insured Person {sortField === 'insured_person' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('start_date')}>
                    Start Date {sortField === 'start_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('end_date')}>
                    End Date {sortField === 'end_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('premium')}>
                    Premium {sortField === 'premium' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('issuance_on')}>
                    Issued On {sortField === 'issuance_on' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {declarations.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="no-data">
                      No declarations found. Declarations will appear here after certificates are issued.
                    </td>
                  </tr>
                ) : (
                  declarations.map((decl) => (
                    <tr key={decl.id}>
                      <td title={decl.row_number || '-'}>{decl.row_number || '-'}</td>
                      <td title={decl.agency_name || 'N/A'}>
                        <span className="cell-text">{decl.agency_name || 'N/A'}</span>
                        {decl.ira_number && (
                          <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.85)', marginTop: '2px' }}>
                            {decl.ira_number}
                          </div>
                        )}
                      </td>
                      <td className="certificate-cell" title={decl.certificate_number || 'N/A'}>
                        <span className="cell-text">{decl.certificate_number || 'N/A'}</span>
                      </td>
                      <td title={decl.policy_number || 'N/A'}>
                        <span className="cell-text">{decl.policy_number || 'N/A'}</span>
                      </td>
                      <td title={decl.certificate_type || 'N/A'}>
                        <span className={`cert-type-badge ${decl.certificate_type?.toLowerCase().includes('type a') ? 'type-a' : 
                          decl.certificate_type?.toLowerCase().includes('type b') ? 'type-b' :
                          decl.certificate_type?.toLowerCase().includes('type c') ? 'type-c' : 'type-d'}`}>
                          <span className="cell-text">{decl.certificate_type || 'N/A'}</span>
                        </span>
                      </td>
                      <td title={decl.vehicle_number || 'N/A'}>
                        <span className="cell-text">{decl.vehicle_number || 'N/A'}</span>
                      </td>
                      <td title={decl.insured_person || 'N/A'}>
                        <span className="cell-text">{decl.insured_person || 'N/A'}</span>
                      </td>
                      <td title={formatDate(decl.start_date)}>
                        <span className="cell-text">{formatDate(decl.start_date)}</span>
                      </td>
                      <td title={formatDate(decl.end_date)}>
                        <span className="cell-text">{formatDate(decl.end_date)}</span>
                      </td>
                      <td className="premium-cell" title={formatCurrency(decl.premium)}>
                        <span className="cell-text">{formatCurrency(decl.premium)}</span>
                      </td>
                      <td title={formatDateTime(decl.issuance_on)}>
                        <span className="cell-text">{formatDateTime(decl.issuance_on)}</span>
                      </td>
                      <td>
                        <button
                          className="view-btn"
                          onClick={() => handleViewDetails(decl)}
                          title="View Details"
                        >
                          👁️ View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page || currentPage} of {pagination.totalPages} ({pagination.total || 0} total)
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                disabled={currentPage === pagination.totalPages}
                className="pagination-btn"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Details Modal */}
      {showModal && selectedDeclaration && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Declaration Details</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Row Number:</label>
                  <span>{selectedDeclaration.row_number || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Certificate Number:</label>
                  <span>{selectedDeclaration.certificate_number || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Ref Number:</label>
                  <span>{selectedDeclaration.ref_number || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Certificate Type:</label>
                  <span>{selectedDeclaration.certificate_type || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Insurance Company:</label>
                  <span>{selectedDeclaration.insurance_company || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Intermediary:</label>
                  <span>{selectedDeclaration.intermediary || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Issued By:</label>
                  <span>{selectedDeclaration.issued_by || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Insured Person:</label>
                  <span>{selectedDeclaration.insured_person || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Issuance On:</label>
                  <span>{formatDateTime(selectedDeclaration.issuance_on)}</span>
                </div>
                <div className="detail-item">
                  <label>Policy Number:</label>
                  <span>{selectedDeclaration.policy_number || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Vehicle Number:</label>
                  <span>{selectedDeclaration.vehicle_number || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Chassis Number:</label>
                  <span>
                    {selectedDeclaration.chassis_number 
                      ? maskChassisNumber(String(selectedDeclaration.chassis_number)) 
                      : 'N/A'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Start Date:</label>
                  <span>{formatDate(selectedDeclaration.start_date)}</span>
                </div>
                <div className="detail-item">
                  <label>End Date:</label>
                  <span>{formatDate(selectedDeclaration.end_date)}</span>
                </div>
                <div className="detail-item">
                  <label>Vehicle Make:</label>
                  <span>{selectedDeclaration.vehicle_make || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Vehicle Model:</label>
                  <span>{selectedDeclaration.vehicle_model || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Body Type:</label>
                  <span>{selectedDeclaration.body_type || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Year of Manufacture:</label>
                  <span>{selectedDeclaration.year_of_manufacture || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Engine Number:</label>
                  <span>
                    {selectedDeclaration.engine_number 
                      ? maskEngineChassisNumber(String(selectedDeclaration.engine_number)) 
                      : 'N/A'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Insured PIN:</label>
                  <span>{selectedDeclaration.insured_pin || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Sum Insured:</label>
                  <span>{formatCurrency(selectedDeclaration.sum_insured)}</span>
                </div>
                <div className="detail-item">
                  <label>Email:</label>
                  <span>{selectedDeclaration.email || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Phone Number:</label>
                  <span>{selectedDeclaration.phone_number || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Certificate Status:</label>
                  <span className={`status-badge ${selectedDeclaration.certificate_status?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
                    {selectedDeclaration.certificate_status || 'N/A'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Premium:</label>
                  <span className="premium-value">{formatCurrency(selectedDeclaration.premium)}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="close-modal-btn" onClick={() => setShowModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default DeclarationsTable;

